/**
 * The symbology registry the block editor, the page renderer and the scanner
 * all read.
 *
 * It is a SECOND declaration of a list keepr-api also holds
 * (utils/cardLayouts/barcodes.js), not a vendored shared module, and that is
 * deliberate: this copy carries editor labels, the bwip-js encoder name and
 * the ZXing format a symbol decodes back as — none of which the server has
 * any use for — while the server's carries only what a WRITE is refused for.
 * Sharing it would mean a sixth byte-compared vendored module for a list of
 * twenty-one strings. What keeps them honest instead is
 * __tests__/symbologyParity.test.js, which reads the API's copy when
 * ../keepr-api is checked out (and skips when it is not, the vendored-test
 * idiom).
 *
 * X-DIMENSION, NOT SCALE. Everything about size here is the width of ONE
 * module in the page's own unit. See ./encode.js for the arithmetic and
 * keepr-api docs/DATA-MODEL.md § cardLayouts for why.
 */

export const FAMILIES = {
	matrix: { label: 'Matrix', hint: 'A square of cells, read in two directions.' },
	stacked: { label: 'Stacked', hint: 'Rows of bars — more data than a linear code, in a wider box.' },
	linear: { label: 'Linear', hint: 'Bars and spaces, swept sideways.' },
};

/** The narrowest module that survives a 203dpi thermal printer, per family. */
export const MODULE_FLOOR = {
	matrix: { in: 0.010, mm: 0.25 },
	stacked: { in: 0.010, mm: 0.25 },
	linear: { in: 0.0067, mm: 0.17 },
};
export const MODULE_CEILING = { in: 0.5, mm: 12 };
/**
 * What a new block gets when its author has not said. Deliberately ABOVE the
 * floor: the floor is where a symbol stops working at all, so shipping it as
 * the default would make every unconfigured label the smallest legal one.
 * 0.013in puts a 29-module QR at about 9.6mm, roughly what a phone wants.
 * (Matched in keepr-api utils/cardLayouts/barcodes.js DEFAULT_MODULE, which
 * is what an absent `module` is stored as.)
 */
export const DEFAULT_MODULE = { in: 0.013, mm: 0.33 };

export const defaultModule = unit => DEFAULT_MODULE[unit === 'mm' ? 'mm' : 'in'];

/**
 * THE OPTION VOCABULARIES ARE BWIPP'S OWN NAMES AND BWIPP'S OWN TYPES.
 *
 * An option this platform offers that the encoder silently ignores is worse
 * than one it does not offer: the author sets it, the editor goes on showing
 * it, and the label prints something else. An option BWIPP throws on is
 * worse still — the whole block draws a red notice. So nothing is in a
 * symbology's `code` list unless BWIPP honours it, and nothing is in a
 * vocabulary unless BWIPP accepts it. ./encode.js maps them one-for-one and
 * __tests__/encode.test.js encodes every symbology x option pair.
 *
 * What is deliberately NOT here, and why:
 *   `encoding` (numeric|alphanumeric|byte|kanji)  BWIPP's QR encoder
 *        segments the payload itself and has no option to pin a mode. Its
 *        `format` names the QR FAMILY (full|micro|rmqr) — already the
 *        symbology — and throws on anything else.
 *   `subset` (Code 128 A|B|C)  BWIPP chooses the code sets itself and
 *        exposes no pin.
 *   `compaction` (text|numeric|byte)  PDF417's `compact` is a BOOLEAN:
 *        Compact PDF417. There is no string compaction option.
 */

/**
 * QR error correction, the letters the standard uses — and NOT the same four
 * everywhere. Micro QR has no H level; rMQR has only M and H. BWIPP does not
 * say so, it simply fails to find a symbol ("Maximum length exceeded or
 * invalid content"), which reads as a payload problem and is not one.
 */
export const ECLEVELS = ['L', 'M', 'Q', 'H'];
export const MICROQR_ECLEVELS = ['L', 'M', 'Q'];
export const RMQR_ECLEVELS = ['M', 'H'];

/** The data-mask, ONE-BASED. Full QR has eight, Micro QR four; 0 is a throw. */
export const QR_MASKS = { min: 1, max: 8 };
export const MICROQR_MASKS = { min: 1, max: 4 };
/** Data Matrix `format` — a symbol SHAPE, not an encoding mode. */
export const SHAPES = ['auto', 'square', 'rectangle'];

/**
 * A `version` is three different vocabularies behind one author-facing word:
 * 1-40 for a full QR, 'M1'-'M4' for a Micro QR, 'R{rows}x{cols}' for an
 * rMQR, and an 'RxC' size for a Data Matrix. Each symbology carries its own
 * descriptor, which is what the inspector renders from and what the API
 * validates against.
 */
export const QR_VERSIONS = { kind: 'int', min: 1, max: 40 };
export const MICROQR_VERSIONS = { kind: 'enum', values: ['M1', 'M2', 'M3', 'M4'] };
export const RMQR_VERSIONS = {
	kind: 'enum',
	values: [
		'R7x43', 'R7x59', 'R7x77', 'R7x99', 'R7x139',
		'R9x43', 'R9x59', 'R9x77', 'R9x99', 'R9x139',
		'R11x27', 'R11x43', 'R11x59', 'R11x77', 'R11x99', 'R11x139',
		'R13x27', 'R13x43', 'R13x59', 'R13x77', 'R13x99', 'R13x139',
		'R15x43', 'R15x59', 'R15x77', 'R15x99', 'R15x139',
		'R17x43', 'R17x59', 'R17x77', 'R17x99', 'R17x139',
	],
};
export const DATAMATRIX_VERSIONS = {
	kind: 'enum',
	values: [
		'10x10', '12x12', '14x14', '16x16', '18x18', '20x20', '22x22', '24x24',
		'26x26', '32x32', '36x36', '40x40', '44x44', '48x48', '52x52', '64x64',
		'72x72', '80x80', '88x88', '96x96', '104x104', '120x120', '132x132', '144x144',
		'8x18', '8x32', '12x26', '12x36', '16x36', '16x48',
	],
};

/** Aztec error correction is a PERCENTAGE of the symbol, 5 to 95. */
export const ECPERCENT_RANGE = { min: 5, max: 95 };

/**
 * The shortest a LINEAR symbol's bars may be and still be swept by a reader.
 * Enforced by the renderer rather than by the API, because it depends on the
 * ROTATION — a symbol turned a quarter turn takes its bar height from the
 * block's width, not its height.
 */
export const BAR_HEIGHT_FLOOR = { in: 0.1, mm: 2.5 };

const DIGITS = /^[0-9]+$/;
const CODE39_CHARSET = /^[0-9A-Z\-. $/+%]*$/;
const CODABAR_CHARSET = /^[A-Da-d][0-9\-$:/.+]*[A-Da-d]$|^[0-9\-$:/.+]*$/;
// eslint-disable-next-line no-control-regex
const ASCII = /^[\x00-\x7F]*$/;

/**
 * Each entry:
 *   label       what the editor calls it
 *   group       the optgroup it sits under
 *   family      matrix | stacked | linear — decides the module floor and
 *               whether the box is aspect-locked
 *   bcid        the bwip-js / BWIPP encoder name (and the named export of
 *               `bwip-js/generic` we re-export in ./encoders.js)
 *   quiet       the default quiet zone, in MODULES
 *   aspectLocked  a matrix symbol dragged into a rectangle is unreadable, so
 *               the editor holds it square while it is dragged
 *   code        the `block.code` keys this symbology reads — the editor
 *               shows exactly these fields and the API refuses the rest
 *   zxing       the format name it decodes back as, or null when nothing we
 *               ship can read it (MSI)
 *   gs1         its payload is application-identifier syntax, so interpolated
 *               values are escaped before the encoder sees them
 *   rule        what a LITERAL payload has to look like
 *   note        the sentence the editor prints under the picker
 */
export const SYMBOLOGIES = {
	qrcode: {
		label: 'QR Code', group: 'QR', family: 'matrix', bcid: 'qrcode', quiet: 4, aspectLocked: true,
		code: ['eclevel', 'version', 'mask'],
		versions: QR_VERSIONS, eclevels: ECLEVELS, masks: QR_MASKS, zxing: 'qr_code',
		note: 'ISO/IEC 18004. The general-purpose choice; needs about 10mm to be read by a phone.',
	},
	microqrcode: {
		label: 'Micro QR', group: 'QR', family: 'matrix', bcid: 'microqrcode', quiet: 2, aspectLocked: true,
		code: ['eclevel', 'version', 'mask'],
		versions: MICROQR_VERSIONS, eclevels: MICROQR_ECLEVELS, masks: MICROQR_MASKS, zxing: 'micro_qr_code',
		note: 'A smaller QR for short payloads — up to 35 digits. Its versions are M1 to M4, and it has no H correction level.',
	},
	rectangularmicroqrcode: {
		label: 'Rectangular Micro QR (rMQR)', group: 'QR', family: 'matrix', bcid: 'rectangularmicroqrcode', quiet: 2, aspectLocked: false,
		code: ['eclevel', 'version'],
		versions: RMQR_VERSIONS, eclevels: RMQR_ECLEVELS, autoVersion: true, zxing: 'rm_qr_code',
		note: 'A long, shallow QR for a narrow label — a cable flag, a pen barrel. Leave the size unset and the smallest one the value fits is chosen.',
	},
	gs1qrcode: {
		label: 'GS1 QR Code', group: 'QR', family: 'matrix', bcid: 'gs1qrcode', quiet: 4, aspectLocked: true, gs1: true,
		code: ['eclevel', 'version', 'mask'],
		versions: QR_VERSIONS, eclevels: ECLEVELS, masks: QR_MASKS, zxing: 'qr_code',
		note: 'A QR carrying GS1 application identifiers — (01) GTIN, (17) expiry, (10) lot.',
	},
	gs1dlqrcode: {
		label: 'GS1 Digital Link QR', group: 'QR', family: 'matrix', bcid: 'gs1dlqrcode', quiet: 4, aspectLocked: true, gs1: true,
		code: ['eclevel', 'version', 'mask'],
		versions: QR_VERSIONS, eclevels: ECLEVELS, masks: QR_MASKS, zxing: 'qr_code',
		note: 'One symbol that is both a resolvable https link a person can follow and a GS1 identifier a scanner can read. Its payload must start with https://.',
	},
	datamatrix: {
		label: 'Data Matrix', group: 'Data Matrix', family: 'matrix', bcid: 'datamatrix', quiet: 1, aspectLocked: true,
		code: ['shape', 'version'], versions: DATAMATRIX_VERSIONS, zxing: 'data_matrix',
		note: 'ISO/IEC 16022. The direct-part-marking standard: readable at 2-3mm where a QR needs about 10mm.',
	},
	gs1datamatrix: {
		label: 'GS1 Data Matrix', group: 'Data Matrix', family: 'matrix', bcid: 'gs1datamatrix', quiet: 1, aspectLocked: true, gs1: true,
		code: ['shape', 'version'], versions: DATAMATRIX_VERSIONS, zxing: 'data_matrix',
		note: 'Data Matrix carrying GS1 application identifiers — the healthcare and aerospace marking standard.',
	},
	pdf417: {
		label: 'PDF417', group: 'Stacked', family: 'stacked', bcid: 'pdf417', quiet: 2, aspectLocked: false,
		code: ['columns', 'rows', 'eclevel417', 'compact'], zxing: 'pdf417',
		note: 'The AAMVA symbology on the back of every US driver’s licence. Wide and shallow; holds a lot.',
	},
	micropdf417: {
		label: 'MicroPDF417', group: 'Stacked', family: 'stacked', bcid: 'micropdf417', quiet: 1, aspectLocked: false,
		code: ['columns', 'rows'], zxing: 'micro_pdf417',
		note: 'PDF417 for a small label.',
	},
	azteccode: {
		label: 'Aztec', group: 'Aztec', family: 'matrix', bcid: 'azteccode', quiet: 0, aspectLocked: true,
		code: ['ecpercent', 'layers'], zxing: 'aztec',
		note: 'ISO/IEC 24778. Needs NO quiet zone, which makes it the smallest symbol that fits a genuinely tiny label. Its error correction is a percentage, not a letter.',
	},
	code128: {
		label: 'Code 128', group: 'Linear', family: 'linear', bcid: 'code128', quiet: 10, aspectLocked: false,
		code: ['text'], zxing: 'code_128',
		rule: { charset: ASCII, charsetLabel: 'ASCII characters' },
		note: 'The dense general-purpose linear code. Subsets A, B and C are chosen by the encoder and cannot be pinned.',
	},
	'gs1-128': {
		label: 'GS1-128', group: 'Linear', family: 'linear', bcid: 'gs1_128', quiet: 10, aspectLocked: false, gs1: true,
		code: ['text'], zxing: 'code_128',
		note: 'Code 128 carrying GS1 application identifiers — the shipping-label standard.',
	},
	code39: {
		label: 'Code 39', group: 'Linear', family: 'linear', bcid: 'code39', quiet: 10, aspectLocked: false,
		code: ['checkdigit', 'text'], zxing: 'code_39',
		rule: { charset: CODE39_CHARSET, charsetLabel: 'digits, capitals, and - . $ / + % or a space' },
		checkdigits: ['none', 'mod43'],
		note: 'Upper case and digits only. Older readers all speak it; mod-43 adds a check character.',
	},
	code93: {
		label: 'Code 93', group: 'Linear', family: 'linear', bcid: 'code93', quiet: 10, aspectLocked: false,
		code: ['text'], zxing: 'code_93',
		rule: { charset: CODE39_CHARSET, charsetLabel: 'digits, capitals, and - . $ / + % or a space' },
		note: 'Code 39’s character set in a denser symbol, with two check characters built in.',
	},
	itf14: {
		label: 'ITF-14', group: 'Linear', family: 'linear', bcid: 'itf14', quiet: 10, aspectLocked: false,
		code: ['text'], zxing: 'itf',
		rule: { digits: [13, 14], label: 'ITF-14' },
		note: 'The carton code: 14 digits, printed with a bearer bar.',
	},
	ean13: {
		label: 'EAN-13', group: 'Retail', family: 'linear', bcid: 'ean13', quiet: 11, aspectLocked: false,
		code: ['text'], zxing: 'ean_13',
		rule: { digits: [12, 13], label: 'EAN-13' },
		note: 'The retail article number. 12 digits and the check digit is added, or 13 with it.',
	},
	ean8: {
		label: 'EAN-8', group: 'Retail', family: 'linear', bcid: 'ean8', quiet: 7, aspectLocked: false,
		code: ['text'], zxing: 'ean_8',
		rule: { digits: [7, 8], label: 'EAN-8' },
		note: 'EAN for a small pack.',
	},
	upca: {
		label: 'UPC-A', group: 'Retail', family: 'linear', bcid: 'upca', quiet: 9, aspectLocked: false,
		code: ['text'], zxing: 'upc_a',
		rule: { digits: [11, 12], label: 'UPC-A' },
		note: 'The North American retail code.',
	},
	upce: {
		label: 'UPC-E', group: 'Retail', family: 'linear', bcid: 'upce', quiet: 9, aspectLocked: false,
		code: ['text'], zxing: 'upc_e',
		rule: { digits: [7, 8], label: 'UPC-E' },
		note: 'A zero-suppressed UPC-A for a small pack.',
	},
	rationalizedCodabar: {
		label: 'Codabar', group: 'Linear', family: 'linear', bcid: 'rationalizedCodabar', quiet: 10, aspectLocked: false,
		code: ['checkdigit', 'text'], zxing: 'codabar',
		rule: { charset: CODABAR_CHARSET, charsetLabel: 'digits, - $ : / . + and an A-D start/stop pair' },
		checkdigits: ['none', 'mod16'],
		note: 'Blood banks, libraries, film. Starts and stops with a letter A-D.',
	},
	msi: {
		label: 'MSI Plessey', group: 'Linear', family: 'linear', bcid: 'msi', quiet: 12, aspectLocked: false,
		code: ['checkdigit', 'text'], zxing: null,
		rule: { charset: DIGITS, charsetLabel: 'digits' },
		checkdigits: ['none', 'mod10', 'mod11', 'mod1010', 'mod1110'],
		note: 'Shelf labels. PRINTS but cannot be scanned back — neither the phone camera nor our fallback reader knows it.',
	},
};

export const SYMBOLOGY_IDS = Object.keys(SYMBOLOGIES);

/** The picker's options, grouped the way an author thinks about them. */
export const SYMBOLOGY_GROUPS = SYMBOLOGY_IDS.reduce((groups, id) => {
	const { group, label } = SYMBOLOGIES[id];
	const bucket = groups.find(g => g.label === group) || (groups.push({ label: group, options: [] }), groups[groups.length - 1]);
	bucket.options.push({ value: id, label });
	return groups;
}, []);

export const symbologyOf = id => SYMBOLOGIES[id] || null;

export const DEFAULT_SYMBOLOGY = 'qrcode';

/** The narrowest module this symbology can print at, in `unit`. */
export const moduleFloor = (id, unit) => {
	const spec = SYMBOLOGIES[id];
	return MODULE_FLOOR[spec ? spec.family : 'linear'][unit === 'mm' ? 'mm' : 'in'];
};

export const defaultQuiet = id => (SYMBOLOGIES[id] ? SYMBOLOGIES[id].quiet : 4);

/**
 * Is the box square by construction? A matrix symbol dragged into a
 * rectangle either distorts or leaves the box lying about what prints — so
 * the editor holds it square while it is dragged. A Data Matrix asked for
 * the RECTANGLE shape is the exception: that is exactly what it is for.
 */
export const isAspectLocked = (id, code = null) => {
	const spec = SYMBOLOGIES[id];
	if (!spec || !spec.aspectLocked) return false;
	if (spec.code.includes('shape') && code && code.shape === 'rectangle') return false;
	return true;
};

/** The `version` vocabulary this symbology speaks, or null for none. */
export const versionsOf = id => (SYMBOLOGIES[id] && SYMBOLOGIES[id].versions) || null;

/** The error-correction letters this symbology offers. */
export const eclevelsOf = id => (SYMBOLOGIES[id] && SYMBOLOGIES[id].eclevels) || ECLEVELS;

/** The data-mask range this symbology offers. */
export const masksOf = id => (SYMBOLOGIES[id] && SYMBOLOGIES[id].masks) || QR_MASKS;

/** The shortest usable bar height for a linear symbol, in `unit`. */
export const barHeightFloor = unit => BAR_HEIGHT_FLOOR[unit === 'mm' ? 'mm' : 'in'];

/**
 * The `code` keys that survive a change of symbology. Anything the new
 * symbology does not own is dropped rather than carried: the API refuses a
 * stray key outright, so carrying one would leave the editor holding work it
 * cannot save.
 */
export const pruneCodeForSymbology = (code, id) => {
	const spec = SYMBOLOGIES[id];
	if (!code || !spec) return undefined;
	const kept = {};
	spec.code.forEach(key => {
		if (code[key] !== undefined && code[key] !== null && code[key] !== '') kept[key] = code[key];
	});
	return Object.keys(kept).length ? kept : undefined;
};

/**
 * What a LITERAL payload has to look like. Returns a message or null.
 *
 * This is the same table keepr-api's barcodes.js holds, for the same reason
 * the id list is duplicated — and it runs in a different PLACE: the API can
 * only judge a payload with no `{{token}}` in it, while here the template has
 * already been rendered against a real item, so every payload is literal by
 * the time it arrives.
 */
export function checkPayload(id, payload) {
	const spec = SYMBOLOGIES[id];
	if (!spec || !spec.rule) return null;
	const { digits, label, charset, charsetLabel } = spec.rule;
	const text = String(payload == null ? '' : payload);
	if (digits) {
		if (!DIGITS.test(text)) return `${label} encodes digits only.`;
		if (!digits.includes(text.length)) return `${label} needs ${digits.join(' or ')} digits; this is ${text.length}.`;
		return null;
	}
	if (charset && !charset.test(text)) return `This symbology encodes ${charsetLabel}.`;
	return null;
}

/**
 * Every format the scanner can read back — the ZXing / BarcodeDetector
 * spelling, which is what the browser API speaks, and the same list the API
 * validates `options.scan.formats` against (keepr-api utils/typedElements.js
 * SCAN_FORMATS).
 *
 * It is a SUPERSET of what we print, on purpose: GS1 DataBar is on every
 * piece of produce in the world and a reader will meet one whether or not
 * this platform can lay one out. MSI is the other direction — we print it
 * and nothing we ship decodes it — which is why it is absent here and
 * flagged in the editor.
 */
export const SCAN_FORMATS = [
	'aztec', 'codabar', 'code_39', 'code_93', 'code_128', 'data_matrix',
	'databar', 'databar_expanded', 'ean_8', 'ean_13', 'itf',
	'micro_pdf417', 'micro_qr_code', 'pdf417', 'qr_code', 'rm_qr_code',
	'upc_a', 'upc_e',
];

/** Every format the platform can also PRINT, for the editor's short list. */
export const PRINTABLE_SCAN_FORMATS = [...new Set(
	SYMBOLOGY_IDS.map(id => SYMBOLOGIES[id].zxing).filter(Boolean)
)].sort();
